/**
 * Minimum token length required for backend searches that rely on a
 * pg_trgm GIN index (Patient, Request, Doctor, Befund). Tokens shorter
 * than this are dropped on the backend, so the frontend should warn
 * the user before issuing such a search.
 *
 * For purely in-memory frontend filters there is no performance reason
 * to enforce a minimum length — the default is 1 (= no filter).
 */
export const MIN_TOKEN_LENGTH_PG_TRGM = 3;

export type SearchField<T> = (item: T) => string | string[] | null | undefined;

export type TokenizeOptions = {
  /** Minimum token length. Default 1 (= keep all). Use 3 for queries hitting a pg_trgm-indexed column. */
  minTokenLength?: number;
};

/** Split a search input into normalized tokens (lowercased, whitespace-separated). */
export function tokenize(searchValue: string | null | undefined, { minTokenLength = 1 }: TokenizeOptions = {}): string[] {
  if (!searchValue) return [];
  return searchValue
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(t => t.length >= minTokenLength);
}

/** AND between tokens, OR over fields. Array-valued fields match if any element contains the token. */
export function matchesTokens<T>(item: T, tokens: string[], fields: SearchField<T>[]): boolean {
  if (tokens.length === 0) return true;
  return tokens.every(token =>
    fields.some(field => {
      const v = field(item);
      if (v == null) return false;
      if (Array.isArray(v)) return v.some(s => s?.toLowerCase().includes(token));
      return v.toLowerCase().includes(token);
    })
  );
}

/** Convenience: tokenize + filter in one call. */
export function filterByTokens<T>(
  items: T[],
  searchValue: string | null | undefined,
  fields: SearchField<T>[],
  options?: TokenizeOptions
): T[] {
  const tokens = tokenize(searchValue, options);
  if (tokens.length === 0) return items;
  return items.filter(item => matchesTokens(item, tokens, fields));
}

/**
 * Ant Design `Select.filterOption` adapter. Uses `option.search` or `option.label`
 * (or string children) as the text to match against, with AND-between-tokens semantics.
 *
 * Usage: `<Select showSearch={{ filterOption: antdTokenFilter }}>`
 *
 * Option type is intentionally loose (Record<string, unknown>) to interoperate with
 * AntD's `FilterFunc<DefaultOptionType>` signature where `label` is `ReactNode`.
 */
export function antdTokenFilter(input: string, option?: Record<string, unknown>): boolean {
  const search = option?.search;
  const label = option?.label;
  const children = option?.children;
  const text = typeof search === 'string' ? search : typeof label === 'string' ? label : typeof children === 'string' ? children : '';

  if (isDev() && input.length > 0 && text.length === 0 && option != null) {
    // Surface misconfiguration: an option without a string `search`/`label`/`children` (e.g. JSX label)
    // cannot be filtered. Warn once so the caller can supply an explicit `search` field.
    warnedAntdTokenFilterOptions ??= new WeakSet();
    if (!warnedAntdTokenFilterOptions.has(option)) {
      warnedAntdTokenFilterOptions.add(option);
      // eslint-disable-next-line no-console
      console.warn(
        '[antdTokenFilter] option has no string search/label/children; the filter will never match. ' +
          'Add a string `search` prop to the option.',
        option
      );
    }
  }

  return matchesTokens(text, tokenize(input), [v => v]);
}

let warnedAntdTokenFilterOptions: WeakSet<object> | undefined;

/** Detect dev mode without requiring `@types/node` or vite-specific globals. */
function isDev(): boolean {
  const env = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env?.NODE_ENV;
  return env !== 'production';
}

/**
 * Returns the tokens that would be dropped if the input were sent to a pg_trgm-backed
 * backend search. Use this to show a UX hint "≥3 Zeichen pro Suchbegriff" when at least
 * one token would be dropped.
 */
export function shortTokensDropped(searchValue: string | null | undefined): string[] {
  const all = tokenize(searchValue);
  const kept = new Set(tokenize(searchValue, { minTokenLength: MIN_TOKEN_LENGTH_PG_TRGM }));
  return all.filter(t => !kept.has(t));
}
